/**
 * Inline image embeds in discussion content.
 *
 * An embedded image is an `<img data-attachment-id="…">` whose file is a normal
 * attachment on the message. The `src` stored in content is whatever presigned
 * URL the editor had at the time and expires within the hour, so every render
 * resolves `src` again from the attachment's current `url` (the thread payload
 * carries one for image attachments). Nothing is served from a public bucket.
 */

const EMBED_ATTR = 'data-attachment-id';

/** Attachment ids referenced by embeds in this HTML, in document order, unique. */
export function extractEmbeddedAttachmentIds(html: string): string[] {
  if (!html || !html.includes(EMBED_ATTR)) return [];
  const ids = new Set<string>();
  const re = /data-attachment-id="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) ids.add(m[1]);
  return Array.from(ids);
}

/**
 * Rewrite embed `src`s from the given attachments' current URLs.
 *
 * `dropUnresolved` (default true) removes an embed whose attachment has no URL
 * (deleted, or a list preview that carries no URLs) so the reader never sees a
 * broken image. Pass false when loading content into the editor: the embed must
 * survive even if its URL is momentarily unavailable.
 */
export function resolveEmbeddedImages(
  html: string,
  attachments?: Array<{ id: string; url?: string | null }> | null,
  dropUnresolved = true,
): string {
  if (!html || !html.includes(EMBED_ATTR)) return html;
  const urls = new Map<string, string>();
  for (const a of attachments ?? []) {
    if (a.url) urls.set(a.id, a.url);
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll(`img[${EMBED_ATTR}]`).forEach((img) => {
    const url = urls.get(img.getAttribute(EMBED_ATTR) ?? '');
    if (url) img.setAttribute('src', url);
    else if (dropUnresolved) img.remove();
  });
  return doc.body.innerHTML;
}

/** Attachments not already shown inline, for the strip under a message. */
export function withoutEmbedded<T extends { id: string }>(attachments: T[] | undefined, content: string | undefined): T[] {
  if (!attachments?.length) return [];
  const inline = new Set(extractEmbeddedAttachmentIds(content ?? ''));
  return inline.size ? attachments.filter((a) => !inline.has(a.id)) : attachments;
}
